import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const HIDDEN = { x: 0, y: 0, width: 0, height: 0, opacity: 0 };
const MAX_HEIGHT = 56;
const TARGET_CLASS = "sidebar-pill-target";

const nearlyEqual = (a, b) => Math.abs(a - b) < 0.5;

const samePill = (a, b) =>
  nearlyEqual(a.x, b.x) &&
  nearlyEqual(a.y, b.y) &&
  nearlyEqual(a.width, b.width) &&
  nearlyEqual(a.height, b.height) &&
  a.opacity === b.opacity;

/** Selected blue pill is only for real route links, never section headers / toggles. */
const isSelectableNavLink = (element) =>
  element instanceof Element &&
  (element.matches?.("a.link") || element.matches?.("a.sidebar-dashboard-link"));

/** True when link is inside a closed / collapsing section — must not show pill there. */
const isInsideClosedSection = (element) => {
  const body = element?.closest?.(".sidebar-section-body");
  if (!body) return false;

  const header = body
    .closest?.(".sidebar-section")
    ?.querySelector?.(".sidebar-section-header");
  // Header without .open means accordion is closed (or closing) in expanded sidebar
  if (
    header &&
    !header.classList.contains("open") &&
    !body.closest?.(".sidebar.collapsed")
  ) {
    return true;
  }

  const br = body.getBoundingClientRect();
  // Closed accordion: body height → 0; clipped children still report full rects
  if (br.height < 8) return true;
  const style = window.getComputedStyle(body);
  if (style.opacity === "0" || style.visibility === "hidden") return true;

  // Mid-collapse: link box sticks out past the clipped body (overlaps next header)
  const er = element.getBoundingClientRect();
  if (er.bottom > br.bottom + 1 || er.top < br.top - 1) return true;

  return false;
};

const measureElement = (container, element) => {
  if (!container || !element) return null;

  // Never park the pill on a clipped/hidden item (looks like header "shadow")
  if (isInsideClosedSection(element)) return null;

  const c = container.getBoundingClientRect();
  const r = element.getBoundingClientRect();

  if (r.width < 2 || r.height < 2) return null;
  if (r.height > MAX_HEIGHT * 1.5) return null;

  // Reject if the link itself is not actually visible in the nav viewport
  const nav = container.getBoundingClientRect();
  if (r.bottom <= nav.top || r.top >= nav.bottom) return null;

  return {
    x: r.left - c.left + container.scrollLeft,
    y: r.top - c.top + container.scrollTop,
    width: r.width,
    height: Math.min(r.height, MAX_HEIGHT),
    opacity: 1,
  };
};

const setTargetClass = (container, element) => {
  if (!container) return;
  container.querySelectorAll(`.${TARGET_CLASS}`).forEach((node) => {
    if (node !== element) node.classList.remove(TARGET_CLASS);
  });
  if (element) element.classList.add(TARGET_CLASS);
};

export const useSidebarActivePill = (containerRef, deps = []) => {
  const [pill, setPill] = useState(HIDDEN);
  const pillRef = useRef(HIDDEN);
  const pendingHrefRef = useRef(null);
  const rafRef = useRef(0);
  const timersRef = useRef([]);
  const scheduleRef = useRef(() => {});

  const commit = useCallback((next) => {
    if (!next) return;
    setPill((prev) => {
      if (samePill(prev, next)) return prev;
      pillRef.current = next;
      return next;
    });
  }, []);

  const placeOn = useCallback(
    (element) => {
      const container = containerRef.current;
      if (!isSelectableNavLink(element)) return false;
      // Extra guard: never on section headers
      if (
        element.classList.contains("sidebar-section-header") ||
        element.closest(".sidebar-section-header")
      ) {
        return false;
      }
      const next = measureElement(container, element);
      if (!next) return false;
      setTargetClass(container, element);
      commit(next);
      return true;
    },
    [commit, containerRef]
  );

  const hidePill = useCallback(() => {
    const container = containerRef.current;
    setTargetClass(container, null);
    commit(HIDDEN);
  }, [commit, containerRef]);

  const applyMeasure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const pathname = window.location.pathname || "";
    const onErrorCodes =
      pathname.startsWith("/user/error-code") ||
      pathname.startsWith("/smsc/error-code");
    const isUsersHref = (href) =>
      href === "/user" ||
      href === "/user/" ||
      href === "/user/list" ||
      href.startsWith("/user/list/") ||
      href === "/user/limit" ||
      href.startsWith("/user/limit/");

    // On Error Codes: always prefer that link; never leave pill on Users
    if (onErrorCodes) {
      pendingHrefRef.current = null;
      const errorLink =
        container.querySelector('a.link[href="/user/error-code"]') ||
        container.querySelector('a.link[href="/smsc/error-code"]') ||
        container.querySelector("a.link.active[href*='error-code']");
      if (errorLink && placeOn(errorLink)) return;
      // Strip stale Users target class if Error Codes row is clipped/closed
      container.querySelectorAll("a.link.sidebar-pill-target").forEach((node) => {
        const href = node.getAttribute("href") || "";
        if (isUsersHref(href)) node.classList.remove("sidebar-pill-target");
      });
      hidePill();
      return;
    }

    if (pendingHrefRef.current) {
      const pendingHref = pendingHrefRef.current;
      // Stale pending must not keep pill on /user/list while on /user/error-code
      const pendingMatchesPath =
        pathname === pendingHref ||
        (pendingHref !== "/" &&
          pathname.startsWith(pendingHref + "/") &&
          !(isUsersHref(pendingHref) && onErrorCodes));

      if (!pendingMatchesPath || (onErrorCodes && isUsersHref(pendingHref))) {
        pendingHrefRef.current = null;
      } else {
        const pending = container.querySelector(
          `a.link[href="${pendingHref}"]`
        );
        if (pending && placeOn(pending)) {
          if (pending.classList.contains("active")) pendingHrefRef.current = null;
          return;
        }
      }
    }

    // Collapsed icon rail: icon .active styles only — no floating pill/shadow
    if (container.closest?.(".sidebar.collapsed")) {
      pendingHrefRef.current = null;
      hidePill();
      return;
    }

    // Prefer the most specific .active link (longest href).
    // Never let /user or /user/list steal highlight from /user/error-code.
    const actives = [
      ...container.querySelectorAll("a.link.active"),
      ...container.querySelectorAll("a.sidebar-dashboard-link.active"),
    ];
    const pathMatchesHref = (href) => {
      if (!href) return false;
      if (onErrorCodes && isUsersHref(href)) return false;
      if (pathname === href) return true;
      if (href === "/") return false;
      if (!pathname.startsWith(href + "/")) return false;
      // /user is not a parent of Error Codes for selection purposes
      if (href === "/user" || href === "/user/list") {
        const rest = pathname.slice(
          href === "/user" ? "/user/".length : "/user/list/".length
        );
        if (href === "/user") {
          return /^(list|limit|multi-access|abac)(\/|$)/.test(rest);
        }
        return true; // /user/list/...
      }
      return true;
    };
    let active = null;
    let bestLen = -1;
    actives.forEach((el) => {
      const href = el.getAttribute("href") || "";
      if (!href) return;
      if (onErrorCodes && isUsersHref(href)) return;
      const matched = pathMatchesHref(href);
      if (matched && href.length > bestLen) {
        active = el;
        bestLen = href.length;
      }
    });
    // Fallback: longest explicit .active (custom isNavActive), still skip /user on error-code
    if (!active) {
      actives.forEach((el) => {
        const href = el.getAttribute("href") || "";
        if (!href) return;
        if (onErrorCodes && isUsersHref(href)) return;
        if (href.length > bestLen) {
          active = el;
          bestLen = href.length;
        }
      });
    }

    // Dashboard uses its own selected styles inside the top card (avoid double pill)
    if (active?.matches?.("a.sidebar-dashboard-link")) {
      pendingHrefRef.current = null;
      hidePill();
      return;
    }

    if (active && placeOn(active)) {
      pendingHrefRef.current = null;
      return;
    }

    // No selected route link visible — hide pill (do not leave it on headers/toggles)
    hidePill();
  }, [containerRef, hidePill, placeOn]);

  const scheduleMeasure = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        applyMeasure();
      });
    });
  }, [applyMeasure]);

  useEffect(() => {
    scheduleRef.current = scheduleMeasure;
  }, [scheduleMeasure]);

  const snapToElement = useCallback(
    (element) => {
      // Only animate pill onto real nav links (selected pages)
      if (!isSelectableNavLink(element)) {
        scheduleMeasure();
        return;
      }
      // Collapsed: no floating pill
      if (containerRef.current?.closest?.(".sidebar.collapsed")) {
        pendingHrefRef.current = null;
        hidePill();
        return;
      }
      pendingHrefRef.current = element.getAttribute("href");
      placeOn(element);
      scheduleMeasure();
    },
    [containerRef, hidePill, placeOn, scheduleMeasure]
  );

  const pinToElement = useCallback(
    (element) => {
      if (!isSelectableNavLink(element)) {
        scheduleMeasure();
        return;
      }
      pendingHrefRef.current = null;
      placeOn(element);
    },
    [placeOn, scheduleMeasure]
  );

  const anchorPillToSubmenuParent = useCallback(() => {
    // Expanding/collapsing menus must not move the selected pill
    scheduleMeasure();
  }, [scheduleMeasure]);

  const clearSubmenuPillAnchor = useCallback(() => {}, []);

  const clearIntent = useCallback(() => {
    pendingHrefRef.current = null;
  }, []);

  const refreshPill = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [0, 40, 120].map((ms) =>
      setTimeout(() => scheduleRef.current(), ms)
    );
    applyMeasure();
  }, [applyMeasure]);

  useLayoutEffect(() => {
    pendingHrefRef.current = null;
    applyMeasure();
    scheduleMeasure();
    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const onScroll = () => scheduleRef.current();
    const onResize = () => refreshPill();

    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => scheduleRef.current())
        : null;
    ro?.observe(container);

    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(() => scheduleRef.current())
        : null;
    mo?.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    applyMeasure();

    return () => {
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
      mo?.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      timersRef.current.forEach(clearTimeout);
      setTargetClass(container, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    pill,
    refreshPill,
    snapToElement,
    pinToElement,
    anchorPillToSubmenuParent,
    clearSubmenuPillAnchor,
    clearIntent,
  };
};
